import { LitElement, html } from 'lit';
import { displayProgressBarEvent } from '@cof/refi-web-sdk/dist/utils/update-progress-bar.js';
import { TagKey } from '@cof/tag-catalog-refi-user-entry-mfe';
import { fs24PlusCircleFilled } from '@cof/fs-icons';
import { FsIcon } from '@cof/fs-icon-wc/fs-icon.js';
import { style } from './pick-an-app-v2.style.js';
import { DebugService } from '../../../services/debug/debug.service.js';
import '@cof/fs-accordion-wc/fs-accordion.js';
import '@cof/fs-label-badge-wc/fs-label-badge';
import { stratum } from '../../../constants/stratum.js';
import { HostAppEventsService } from '../../../utils/host-app-events-service.js';
import { AppCountsAnalyticsService } from '../../../services/app-counts-analytics/app-counts-analytics.service.js';

class RefiPickAnAppV2 extends LitElement {
  // App status hierarchy tiers for "Active Applications"
  static TIER_1_STATUSES = ['Funding']; // Status 406

  static TIER_2_STATUSES = ['Approved']; // Status 405

  static TIER_3_STATUSES = ['Reviewing', 'Verifying', 'Received', 'Pending']; // Status 403, 404, 400, 408

  static TIER_4_STATUSES = ['Funded']; // Status 407

  // Terminal/unsuccessful statuses for "Other Applications"
  static OTHER_APP_STATUSES = ['Declined', 'Canceled', 'Returned', 'Expired']; // Status 402, 401, 409, 410

  constructor() {
    super();
    this.debug = new DebugService('PickAnAppComponentV2');
    this.apps = [];
    this.applicantFirstName = '';
    this.activeApps = [];
    this.otherApps = [];
    this.loading = false;
    this.experiments = null;
    this.hideAllVehicleImages = false;
    this.allVehicleImagesLoaded = false;
    this._vehicleImageSuccessCount = 0;
    this._onVehicleImageError = this._handleVehicleImageError.bind(this);
    this._onVehicleImageLoadSuccess = this._handleVehicleImageLoadSuccess.bind(this);
    FsIcon.add(fs24PlusCircleFilled);
  }

  static properties = {
    loading: { type: Boolean },
    apps: { type: Array, attribute: 'app-list', reflect: true },
    applicantFirstName: { type: String, attribute: 'applicant-first-name', reflect: true },
    featureToggles: { type: Array },
    experiments: { type: Object },
    activeApps: { type: Array },
    otherApps: { type: Array },
    hideAllVehicleImages: { type: Boolean, state: true },
    allVehicleImagesLoaded: { type: Boolean, state: true },
  };

  firstUpdated() {
    displayProgressBarEvent(false);
  }

  updated(changes) {
    super.updated(changes);
    if (changes.has('apps')) {
      this.categorizeAndSortApps();
      this._vehicleImageSuccessCount = 0;
      this.allVehicleImagesLoaded = false;
      // Publish app counts for analytics using the shared service
      AppCountsAnalyticsService.publishAppCounts(this.apps);
    }
  }

  static get styles() {
    return [style];
  }

  connectedCallback() {
    super.connectedCallback();
    this.loading = false;
    stratum.publishTag(TagKey.PAGE_VIEW_COAF_REFI_USERENTRY_PICK_AN_APP_V2);
    this.categorizeAndSortApps();
    this.addEventListener('vehicle-image-error', this._onVehicleImageError);
    this.addEventListener('vehicle-image-load-success', this._onVehicleImageLoadSuccess);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('vehicle-image-error', this._onVehicleImageError);
    this.removeEventListener('vehicle-image-load-success', this._onVehicleImageLoadSuccess);
  }

  _handleVehicleImageError(e) {
    this.debug.log('Vehicle image error received — hiding all vehicle images', { sourceAppId: e?.detail?.applicationId });
    this.hideAllVehicleImages = true;
    stratum.publishTag(TagKey.PICK_AN_APP_V2_VEHICLE_IMAGES_LOAD_FAILURE, {
      replacements: {
        value: JSON.stringify({
          applicationId: e?.detail?.applicationId,
          applicationStatus: e?.detail?.applicationStatus,
          vehicleDetails: e?.detail?.vehicleDetails,
        }),
      },
    });
  }

  _handleVehicleImageLoadSuccess() {
    this._vehicleImageSuccessCount += 1;
    if (this._vehicleImageSuccessCount === this.activeApps.length) {
      this.debug.log('All vehicle images loaded successfully');
      // Reveal all tiles at once now that every image has resolved.
      this.allVehicleImagesLoaded = true;
      stratum.publishTag(TagKey.PICK_AN_APP_V2_VEHICLE_IMAGES_SUCCESSFUL_LOAD, {
        replacements: { value: JSON.stringify({ count: this._vehicleImageSuccessCount }) },
      });
    }
  }

  /**
   * Determines the tier for an application based on its status
   * @param {Object} app - Application object
   * @returns {number} Tier number (1-4 for active, 5 for other)
   */
  getAppTier(app) {
    const status = app?.applicationStatus;

    if (RefiPickAnAppV2.TIER_1_STATUSES.includes(status)) return 1;
    if (RefiPickAnAppV2.TIER_2_STATUSES.includes(status)) return 2;
    if (RefiPickAnAppV2.TIER_3_STATUSES.includes(status)) return 3;
    if (RefiPickAnAppV2.TIER_4_STATUSES.includes(status)) return 4;
    if (RefiPickAnAppV2.OTHER_APP_STATUSES.includes(status)) return 5;

    // Default to "Other" for unknown statuses
    return 5;
  }

  /**
   * Checks if an app is expired
   * @param {Object} app - Application object
   * @returns {boolean} True if expired
   */
  isAppExpired(app) {
    return app?.expirationTimestamp && new Date(app.expirationTimestamp) <= new Date();
  }

  /**
   * Categorizes apps into "Active Applications" and "Other Applications"
   * and sorts them according to the hierarchy rules
   */
  categorizeAndSortApps() {
    if (!this.apps || !Array.isArray(this.apps) || this.apps.length === 0) {
      this.activeApps = [];
      this.otherApps = [];
      return;
    }

    const active = [];
    const other = [];

    // Categorize apps
    this.apps.forEach(app => {
      const tier = this.getAppTier(app);
      const expired = this.isAppExpired(app);

      // Apps in tiers 1-4 that are not expired go to active
      // Everything else (tier 5 or expired) goes to other
      if (tier <= 4 && !expired) {
        active.push({ ...app, _tier: tier });
      } else {
        other.push({ ...app, _tier: tier });
      }
    });

    // Sort active apps by tier, then by received date (most recent first)
    const sortedActive = active.sort((a, b) => {
      // First, sort by tier
      if (a._tier !== b._tier) {
        return a._tier - b._tier;
      }
      // Within same tier, sort by received date (most recent first)
      const dateA = new Date(a.receivedTimestamp || 0);
      const dateB = new Date(b.receivedTimestamp || 0);
      return dateB - dateA;
    });

    // Sort other apps by received date (most recent first)
    const sortedOther = other.sort((a, b) => {
      const dateA = new Date(a.receivedTimestamp || 0);
      const dateB = new Date(b.receivedTimestamp || 0);
      return dateB - dateA;
    });

    // Assign to properties to trigger reactivity
    this.activeApps = [...sortedActive];
    this.otherApps = [...sortedOther];
  }

  renderHeadline() {
    return html`${this.renderHeadlineMobile()} ${this.renderHeadlineDesktop()}`;
  }

  isApplicantFirstNameAvailable() {
    return this.applicantFirstName && this.applicantFirstName.trim() !== '';
  }

  renderHeadlineMobile() {
    const welcomeMessage = this.isApplicantFirstNameAvailable() ? 'Welcome back,' : 'Welcome back!';

    const nameHeading = this.isApplicantFirstNameAvailable() ? html`<fs-heading weight="light" size="large" variant="primary" level="2">${this.applicantFirstName}!</fs-heading>` : null;

    return html`
      <div class="apps-list-container--header mobile-view">
        <fs-heading weight="light" size="large" variant="primary" level="1">${welcomeMessage}</fs-heading>
        ${nameHeading}
      </div>
    `;
  }

  renderHeadlineDesktop() {
    const welcomeText = this.isApplicantFirstNameAvailable() ? `Welcome back, ${this.applicantFirstName}!` : 'Welcome back!';

    return html`<div class="apps-list-container--header desktop-view">
      <fs-heading weight="light" size="large" variant="primary" level="1"> ${welcomeText} </fs-heading>
    </div> `;
  }

  renderNewApplicationButton() {
    const handleClick = () => {
      this.loading = true;
      stratum.publishTag(TagKey.PICK_AN_APP_V2_START_A_NEW_APPLICATION_BUTTON);
      HostAppEventsService.sendStartNewAppEvent();
    };

    // Button is outlined if there are any active apps
    const hasActiveApps = this.activeApps && this.activeApps.length > 0;

    return html`<fs-button variant="primary" type="button" size="large" icon-position="leading" ?outlined="${hasActiveApps}" ?loading="${this.loading}" class="start-new-application" @click="${handleClick}">
      <fs-icon icon="fs-24-plus-circle-filled" description="Start new application" size="small" class="fs-icon fs-icon--primary fs-icon--interactive"></fs-icon>
      New application</fs-button
    >`;
  }

  renderActiveApps() {
    if (!this.activeApps || this.activeApps.length === 0) {
      return '';
    }

    const showImages = !this.hideAllVehicleImages;
    return html` <div class="active-apps-section" aria-label="Active applications">${this.activeApps.map(app => html`<refi-app-tile app="${JSON.stringify(app)}" ?show-image="${showImages}" ?all-images-loaded="${this.allVehicleImagesLoaded}" .featureToggles="${this.featureToggles}"></refi-app-tile>`)}</div> `;
  }

  renderOtherApps() {
    if (!this.otherApps || this.otherApps.length === 0) {
      return '';
    }

    return html`<fs-accordion icon-position="trailing" ?inset="false" size="medium" class="other-apps" aria-label="Inactive applications">
      <fs-accordion-item
        ?disabled="false"
        ?expanded="false"
        @toggle="${() => {
          stratum.publishTag(TagKey.PICK_AN_APP_V2_OTHER_APPLICATIONS_ACCORDION_TOGGLE);
        }}"
      >
        <span slot="fs-accordion-item-title" class="other-apps--header">Inactive applications</span>
        <section slot="fs-accordion-item-content">${this.otherApps.map(app => html`<refi-app-tile app="${JSON.stringify(app)}" .featureToggles="${this.featureToggles}"></refi-app-tile>`)}</section>
      </fs-accordion-item>
    </fs-accordion>`;
  }

  render() {
    return html`
      <refi-track-pageview pagename="pick-an-app"></refi-track-pageview>
      <div class="apps-list-container" aria-label="Application list page">
        ${this.renderHeadline()}

        <!-- Active Applications section - Displays apps in hierarchical order -->
        ${this.renderActiveApps()}

        <!-- Display CTA to start new application -->
        ${this.renderNewApplicationButton()}

        <!-- Other Applications section - Displays terminal/unsuccessful applications -->
        ${this.renderOtherApps()}
      </div>
    `;
  }
}

customElements.define('refi-pick-an-app-v2', RefiPickAnAppV2);
